/**
 * useTutorApplicationForm — une étape écrite de la candidature (le profil, la motivation, la
 * pédagogie), question par question : la machine (`applicationFormState`) branchée sur ce que
 * faisait l'ancien `FormStepper` autour d'elle.
 *
 *  - **Le brouillon** : les réponses sont gardées à chaque « Suivant » (`saveFormData`, sous le même
 *    `formId` et le même compte que l'ancien formulaire : l'un reprend le brouillon de l'autre),
 *    rendues à l'ouverture avec « ↪️ Dernières données de formulaire restaurées », effacées une fois
 *    l'étape envoyée (`resetFormData`, juste après l'envoi, comme l'ancien).
 *  - **Les mesures** : `form.step.started` à l'arrivée sur chaque question, `form.step.finished` à
 *    chaque « Suivant » ou « Terminer » (avant la validation, comme l'ancien), et
 *    `button.clicked:progress-stepper.exit` à chaque flèche ou croix de l'en-tête. L'étape en
 *    propriété (`formStepName`).
 *  - **Quitter** : la croix de la première question confirme (toujours pour un entretien, une fois
 *    une réponse donnée pour le profil), puis revient en arrière, ou sur `menu` sans arrière.
 *  - **La fin d'étape** : après l'envoi réussi, la page de fin d'étape (le son `finished.mp3` et
 *    `congrats.PERSONAL_INFO_CONGRATS`, la mesure de l'ancien écran de félicitations), et
 *    « Continuer » revient en arrière.
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { resetFormData, saveFormData } from "../../../../../../data/redux/actions/formData"
import { type ChampionName } from "../../../../../../design-system/atoms/Avatar/ChampionAvatar"
import { type TutorApplicationQuestionPageProps } from "../../../../../../design-system/pages/TutorApplicationQuestionPage/TutorApplicationQuestionPage"
import {
    APPLICATION_TOGGLE_LABELS,
    type ApplicationQuestion,
    type ApplicationSchoolSubject,
    type ApplicationToggles,
    type ApplicationValues,
    autoAdvances,
    availableLanguages,
    hasFooterAction,
    optionDisabled,
    proficiencyOf,
    questionStepName,
    listOf,
    type ApplicationLanguageAnswer,
    toUILanguages,
    visibleInputs,
} from "../../../../adapters/tutor/Application/tutorApplicationQuestions"
import { track } from "../../../hooks/trackEvent"
import { type UITutorApplicationAsker } from "../../../types/tutorCandidature"
import { ACTIVITY_SOUNDS } from "../../student/Activity/activitySounds"
import { playStudentSound } from "../../student/studentSounds"
import {
    type ApplicationFormState,
    applicationFormReducer,
    currentQuestion,
    exitNeedsConfirmation,
    initialApplicationForm,
    validateCurrent,
} from "./applicationFormState"

export const RESTORED_DRAFT_MESSAGE = "↪️ Dernières données de formulaire restaurées"
export const STEP_DONE_EVENT = "congrats.PERSONAL_INFO_CONGRATS"

type FormDataState = { data?: { formData?: Record<string, Record<string, ApplicationValues | null> | undefined> } }

type Navigation = { canGoBack: () => boolean; goBack: () => void; replace: (route: string) => void }

export type UseTutorApplicationFormOptions = {
    questions: ApplicationQuestion[]
    formId: "application-personal-infos" | "interview-step"
    entryId: string | number | null | undefined
    /** Un entretien : le champion de l'étape pose les questions. Absent au profil. */
    askedBy?: UITutorApplicationAsker
    exitMessage: string
    /** L'entretien confirme toujours la sortie ; le profil, une fois une réponse donnée. */
    alwaysConfirmExit: boolean
    schoolSubjects?: ApplicationSchoolSubject[] | null
    /** Envoie l'étape ; rend `false` quand rien n'est parti (un envoi déjà en vol). */
    submit: (state: ApplicationFormState) => boolean
    submitting: boolean
    serverError: string | null
    /** Les valeurs par défaut de l'ancien formulaire qui partent telles quelles (les langues : `[]`). */
    defaults?: ApplicationValues
    /** Le champion et sa phrase de fin d'étape. */
    stepDone: { champion: ChampionName; text: string }
}

export function useTutorApplicationForm(options: UseTutorApplicationFormOptions) {
    const { questions, formId, entryId, askedBy, exitMessage, alwaysConfirmExit, schoolSubjects } = options
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as Navigation

    // Le brouillon lu une fois, à l'ouverture : c'est lui que l'ancien formulaire reprenait.
    const draft = useSelector((state: FormDataState) =>
        entryId != null ? (state?.data?.formData?.[formId]?.[String(entryId)] ?? null) : null
    )
    // Les valeurs de départ : celles par défaut de l'ancien formulaire, puis le brouillon par-dessus.
    const startRef = useRef<ApplicationValues>({ ...options.defaults, ...(draft ?? {}) })
    const [state, dispatchForm] = useReducer(applicationFormReducer, startRef.current, initialApplicationForm)
    const [restoredNotice, setRestoredNotice] = useState(draft != null && Object.keys(draft).length > 0)

    const { visible, index, question, isLast } = currentQuestion(questions, state)
    const stepName = question != null ? questionStepName(question) : null

    useEffect(() => {
        if (stepName != null && !state.done) track({ name: "form.step.started", properties: { step: stepName } })
        // Une mesure par question montrée.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepName, index])

    useEffect(() => {
        if (!state.done) return
        track({ name: STEP_DONE_EVENT })
        return playStudentSound(ACTIVITY_SOUNDS.finished, false, "finished")
    }, [state.done])

    const exit = useCallback(() => {
        dispatchForm({ type: "CLOSE_EXIT" })
        if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu")
    }, [navigation])

    const next = useCallback(
        (values: ApplicationValues = state.values) => {
            if (question == null) return
            const current = { ...state, values }
            track({ name: "form.step.finished", properties: { step: questionStepName(question) } })
            if (entryId != null) dispatch(saveFormData(formId, entryId, values) as never)
            const errors = validateCurrent(question, current)
            if (Object.keys(errors).length > 0) {
                dispatchForm({ type: "SET_ERRORS", errors })
                return
            }
            if (!isLast) {
                dispatchForm({ type: "NEXT" })
                return
            }
            if (options.submit(current) && entryId != null) dispatch(resetFormData(formId, entryId) as never)
        },
        [dispatch, entryId, formId, isLast, options, question, state]
    )

    const back = useCallback(() => {
        track({ name: "button.clicked:progress-stepper.exit" })
        if (index > 0) {
            dispatchForm({ type: "BACK" })
            return
        }
        if (exitNeedsConfirmation(alwaysConfirmExit, state, startRef.current)) dispatchForm({ type: "OPEN_EXIT" })
        else exit()
    }, [alwaysConfirmExit, exit, index, state])

    const setValue = (name: string, value: unknown) => dispatchForm({ type: "SET_VALUE", name, value })

    const page = question == null ? null : questionPageProps()

    function questionPageProps(): TutorApplicationQuestionPageProps {
        const q = question as ApplicationQuestion
        const values = state.values
        const toggles: ApplicationToggles = state.toggles
        const toggleKey = q.kind === "single" || q.kind === "text" ? q.toggle : undefined
        const chosen = q.kind === "single" && values[q.name] != null
        const actionLabel = isLast ? "Terminer" : q.kind === "languages" ? "Continuer" : "Suivant"

        return {
            question: q.label,
            askedBy,
            index: index + 1,
            total: visible.length,
            answer: answerFor(q),
            toggle:
                toggleKey != null
                    ? {
                          label: APPLICATION_TOGGLE_LABELS[toggleKey],
                          checked: toggles[toggleKey],
                          onChange: (value) => dispatchForm({ type: "SET_TOGGLE", key: toggleKey, value }),
                          note: q.kind === "text" ? q.note : undefined,
                      }
                    : undefined,
            precision:
                q.kind === "single" && q.precision != null && chosen
                    ? {
                          placeholder: q.precision.placeholder,
                          value: state.precisions[q.name] ?? "",
                          onChange: (text) => dispatchForm({ type: "SET_PRECISION", name: q.name, value: text }),
                      }
                    : undefined,
            action: hasFooterAction({ question: q, values, toggles })
                ? { label: actionLabel, loading: isLast && options.submitting, onPress: () => next() }
                : undefined,
            error: isLast
                ? (options.serverError ?? state.errors.foreign_languages ?? null)
                : (state.errors.foreign_languages ?? null),
            onBack: index > 0 ? back : undefined,
            onClose: index === 0 ? back : undefined,
            exitConfirm: {
                visible: state.exitOpen,
                message: exitMessage,
                onConfirm: exit,
                onCancel: () => dispatchForm({ type: "CLOSE_EXIT" }),
            },
        }
    }

    function answerFor(q: ApplicationQuestion): TutorApplicationQuestionPageProps["answer"] {
        const values = state.values
        switch (q.kind) {
            case "single":
                return {
                    kind: "choice",
                    layout: q.layout,
                    options: q.options.map((option) => ({
                        id: String(option.value),
                        label: option.label,
                        detail: option.detail,
                    })),
                    selectedIds: values[q.name] != null ? [String(values[q.name])] : [],
                    onToggle: (id) => {
                        const option = q.options.find((candidate) => String(candidate.value) === id)
                        if (option == null) return
                        const nextValues = { ...values, [q.name]: option.value }
                        setValue(q.name, option.value)
                        if (autoAdvances(q, isLast)) next(nextValues)
                    },
                }
            case "multiple": {
                const chosen = listOf(values[q.name])
                return {
                    kind: "choice",
                    options: q.options.map((option) => ({
                        id: String(option.value),
                        label: option.label,
                        detail: option.detail,
                        disabled: optionDisabled(q, values, option.value),
                    })),
                    selectedIds: chosen.map(String),
                    onToggle: (id) => {
                        const option = q.options.find((candidate) => String(candidate.value) === id)
                        if (option == null || optionDisabled(q, values, option.value)) return
                        setValue(
                            q.name,
                            chosen.includes(option.value)
                                ? chosen.filter((value) => value !== option.value)
                                : [...chosen, option.value]
                        )
                    },
                }
            }
            case "text":
                return {
                    kind: "fields",
                    fields: visibleInputs(q, state.toggles).map((input) => ({
                        id: input.name,
                        label: input.label,
                        placeholder: input.placeholder,
                        value: String(values[input.name] ?? ""),
                        error: state.errors[input.name],
                        keyboard: input.keyboard,
                    })),
                    onChange: (id, value) => setValue(id, value),
                }
            case "languages": {
                const available = availableLanguages(schoolSubjects)
                const declared = listOf<ApplicationLanguageAnswer>(values.foreign_languages)
                return {
                    kind: "languages",
                    languages: toUILanguages(values.foreign_languages, available),
                    available: available.filter(
                        (language) => !declared.some((lang) => String(lang.id) === language.id)
                    ),
                    // L'identifiant d'une matière est un nombre : il part tel que l'ancien l'envoyait.
                    onAdd: (id) => setValue("foreign_languages", [...declared, { id: subjectId(schoolSubjects, id) }]),
                    onRemove: (id) =>
                        setValue(
                            "foreign_languages",
                            declared.filter((lang) => String(lang.id) !== id)
                        ),
                    onChangeLevel: (id, level) =>
                        setValue(
                            "foreign_languages",
                            declared.map((lang) =>
                                String(lang.id) === id ? { ...lang, proficiency: proficiencyOf(level) } : lang
                            )
                        ),
                    showErrors: state.errors.foreign_languages != null,
                }
            }
        }
    }

    return {
        page,
        done: state.done,
        markDone: () => dispatchForm({ type: "DONE" }),
        stepDone: { ...options.stepDone, onNext: () => navigation.goBack() },
        restoredNotice: {
            visible: restoredNotice,
            message: RESTORED_DRAFT_MESSAGE,
            onDismiss: () => setRestoredNotice(false),
        },
    }
}

function subjectId(subjects: ApplicationSchoolSubject[] | null | undefined, id: string): number | string {
    return (subjects ?? []).find((subject) => String(subject.id) === id)?.id ?? id
}
